import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { listingsAPI, Listing, paymentsAPI, shippingAddressesAPI, deliveryFeeAPI, locationsAPI } from '../api';
import { Stripe } from '@stripe/stripe-js';
import { Elements, AddressElement } from '@stripe/react-stripe-js';
import CardPaymentForm from '../components/CardPaymentForm';
import { initStripeCheckout } from '../utils/loadStripeClient';
import AddressFormWithAutocomplete from '../components/AddressFormWithAutocomplete';
import HomeLocationModal from '../components/HomeLocationModal';
import {
  addressStreetLineOnly,
  isCompleteDeliveryAddress,
  isCompleteSavedAddress,
  isDuplicateAddress,
} from '../utils/addressUtils';
import {
  calculateCheckoutTotal,
  calculateDeliveryFeeIos,
  calculateSalesTaxAmount,
  resolveTaxStateIos,
} from '../utils/salesTax';
import { calculateCheckoutSubtotal, checkoutLineTotal } from '../utils/listingPricing';
import { extractPostalCodeFromAddress } from '../utils/addressUtils';

// Calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const Checkout: React.FC = () => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeConfigReady, setStripeConfigReady] = useState(false);
  const [stripeMode, setStripeMode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    initStripeCheckout()
      .then(({ stripe, mode }) => {
        if (cancelled) return;
        setStripeMode(mode);
        setStripePromise(stripe ? Promise.resolve(stripe) : null);
        setStripeConfigReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setStripePromise(null);
          setStripeConfigReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [listing, setListing] = useState<Listing | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  // Single-page checkout (no multi-step)
  const [formData, setFormData] = useState({
    fullName: '',
    deliveryAddress: '',
    deliveryInstructions: '',
    deliveryPreference: 'leave_at_door' as 'leave_at_door' | 'meet_at_door',
    leaveLocation: '' as '' | 'apartment_door' | 'lobby' | 'office_suite' | 'room_door',
    phoneNumber: '',
    paymentMethod: 'card',
    addressData: null as { line1: string; city: string; state: string; postal_code: string; country: string; formatted: string } | null,
    addressType: null as 'house' | 'apartment' | 'hotel' | 'office' | 'other' | null,
    aptSuite: '',
    entryCode: '',
    buildingName: '',
    gateCode: '',
    businessName: '',
    hotelName: '',
    roomSuite: '',
  });
  const [addressStep, setAddressStep] = useState<'address' | 'type' | 'details' | 'done'>('address');
  const [error, setError] = useState('');
  const [savedAddresses, setSavedAddresses] = useState<any[]>([]);
  const [saveAddress, setSaveAddress] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(isAuthenticated);
  const [addressComplete, setAddressComplete] = useState(false);
  const [addressValue, setAddressValue] = useState<any>(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [deliveryOriginUsed, setDeliveryOriginUsed] = useState<string | null>(null);
  const [deliveryAvailable, setDeliveryAvailable] = useState<boolean | null>(null);
  const [calculatingDelivery, setCalculatingDelivery] = useState(false);
  const [deliveryCalculationFailed, setDeliveryCalculationFailed] = useState(false);
  const [resolvedStateFromZip, setResolvedStateFromZip] = useState<string | null>(null);

  const loadSavedAddresses = React.useCallback(async () => {
    if (!isAuthenticated) {
      setLoadingAddresses(false);
      return;
    }
    setLoadingAddresses(true);
    try {
      let raw: any = await shippingAddressesAPI.getAll();
      let addresses: any[] = Array.isArray(raw) ? raw : [];
      if (addresses.length === 1 && !addresses[0]?.is_default) {
        try { await shippingAddressesAPI.update(addresses[0].id, { is_default: true }); } catch (_) {}
        const refreshed = await shippingAddressesAPI.getAll();
        addresses = Array.isArray(refreshed) ? refreshed : [];
      }
      setSavedAddresses(addresses);
      
      const buildAddressFromParts = (addr: any) => {
        const raw = (addr?.address || addr?.formatted || '').trim();
        if (raw) return raw;
        const parts = [(addr?.line1 || '').trim(), (addr?.city || '').trim(), (addr?.state || '').trim(), (addr?.postal_code || '').trim()].filter(Boolean);
        if (parts.length === 0) return '';
        const country = (addr?.country || 'US').trim();
        return parts.join(', ') + (country && country !== 'US' ? `, ${country}` : ', USA');
      };

      // Set default address if available and complete (skip city-only / geolocation saves)
      const defaultAddress =
        addresses.find((addr: any) => addr.is_default && isCompleteSavedAddress(addr)) ||
        addresses.find((addr: any) => isCompleteSavedAddress(addr));
      if (defaultAddress) {
        const fmt = buildAddressFromParts(defaultAddress);
        setSelectedAddressId(defaultAddress.id);
        setFormData(prev => ({
          ...prev,
          deliveryAddress: fmt,
          deliveryInstructions: defaultAddress.instructions || '',
          phoneNumber: defaultAddress.phone_number || '',
          addressType: defaultAddress.address_type || null,
          aptSuite: defaultAddress.apt_suite || '',
          entryCode: defaultAddress.entry_code || '',
          buildingName: defaultAddress.building_name || '',
          addressData: {
            line1: defaultAddress.line1 || fmt.split(',')[0]?.trim() || fmt,
            city: defaultAddress.city || '',
            state: defaultAddress.state || '',
            postal_code: defaultAddress.postal_code || '',
            country: defaultAddress.country || 'US',
            formatted: fmt
          }
        }));
        setShowAddressForm(false);
        setSavedAddressVersion(v => v + 1);
      } else if (addresses.length > 0) {
        const a = addresses[0];
        const fmt = buildAddressFromParts(a);
        setSelectedAddressId(a.id);
        setFormData(prev => ({
          ...prev,
          deliveryAddress: fmt,
          deliveryInstructions: a.instructions || '',
          phoneNumber: a.phone_number || '',
          addressType: a.address_type || null,
          aptSuite: a.apt_suite || '',
          entryCode: a.entry_code || '',
          buildingName: a.building_name || '',
          addressData: {
            line1: a.line1 || fmt.split(',')[0]?.trim() || fmt,
            city: a.city || '',
            state: a.state || '',
            postal_code: a.postal_code || '',
            country: a.country || 'US',
            formatted: fmt
          }
        }));
        setShowAddressForm(false);
        setSavedAddressVersion(v => v + 1);
        // Don't auto-advance - let user review and edit if needed
      } else {
        // No saved addresses, show form
        setShowAddressForm(true);
        setSelectedAddressId(null);
      }
    } catch (err) {
      console.error('Error loading saved addresses:', err);
      // If error, show form
      setShowAddressForm(true);
    } finally {
      setLoadingAddresses(false);
    }
  }, [isAuthenticated]);

  // Pre-fill full name from user profile when logged in
  useEffect(() => {
    if (isAuthenticated && user?.username && !formData.fullName.trim()) {
      setFormData(prev => ({ ...prev, fullName: user.username }));
    }
  }, [isAuthenticated, user?.username]);

  useEffect(() => {
    if (id) {
      loadListing();
    } else if (location.state?.listings && Array.isArray(location.state.listings)) {
      const list = location.state.listings as Listing[];
      const listingsWithShipping = list.map((l: Listing) => ({
        ...l,
        shipping_fee: l.shipping_fee !== undefined && l.shipping_fee !== null ? l.shipping_fee : 2.99
      }));
      setListings(listingsWithShipping);
      setLoading(false);
      // Refetch first listing by id so we have full data (e.g. address) for distance calculation
      if (list.length > 0 && list[0].id != null) {
        listingsAPI.getOne(list[0].id).then((full) => {
          const fullWithShipping = { ...full, shipping_fee: full.shipping_fee !== undefined && full.shipping_fee !== null ? full.shipping_fee : 2.99 };
          setListing(fullWithShipping);
          setListings(prev => [fullWithShipping, ...prev.slice(1)]);
        }).catch(() => {});
      }
    } else if (location.state?.listing) {
      const stateListing = location.state.listing as Listing;
      const listingWithShipping = {
        ...stateListing,
        shipping_fee: stateListing.shipping_fee !== undefined && stateListing.shipping_fee !== null ? stateListing.shipping_fee : 2.99
      };
      setListing(listingWithShipping);
      setListings([listingWithShipping]);
      setLoading(false);
      if (stateListing.id != null) {
        listingsAPI.getOne(stateListing.id).then((full) => {
          const withShipping = { ...full, shipping_fee: full.shipping_fee !== undefined && full.shipping_fee !== null ? full.shipping_fee : 2.99 };
          setListing(withShipping);
          setListings([withShipping]);
        }).catch(() => {});
      }
    } else {
      // Resume Buy now after login (guest was sent to /login → /checkout).
      let pending: Listing[] | null = null;
      try {
        const raw = sessionStorage.getItem('pendingCheckoutListings');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) pending = parsed;
        }
      } catch {
        pending = null;
      }
      if (pending) {
        try {
          sessionStorage.removeItem('pendingCheckoutListings');
        } catch {
          /* ignore */
        }
        const listingsWithShipping = pending.map((l: Listing) => ({
          ...l,
          shipping_fee: l.shipping_fee !== undefined && l.shipping_fee !== null ? l.shipping_fee : 2.99,
        }));
        setListings(listingsWithShipping);
        setLoading(false);
        if (pending[0]?.id != null) {
          listingsAPI.getOne(pending[0].id).then((full) => {
            const fullWithShipping = {
              ...full,
              shipping_fee: full.shipping_fee !== undefined && full.shipping_fee !== null ? full.shipping_fee : 2.99,
            };
            setListing(fullWithShipping);
            setListings((prev) => [fullWithShipping, ...prev.slice(1)]);
          }).catch(() => {});
        }
      } else {
        navigate('/');
      }
    }
    
    if (isAuthenticated) {
      loadSavedAddresses();
    }
  }, [id, navigate, location, isAuthenticated]);

  const loadListing = async () => {
    if (!id) return;
    try {
      const data = await listingsAPI.getOne(parseInt(id));
      setListing(data);
      setListings([data]);
    } catch (err: any) {
      setError('Failed to load listing');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const formatPrice = React.useCallback((price: number | null | undefined) => {
    if (price == null) return 'Free';
    return `$${Number(price).toFixed(2)}`;
  }, []);

  // Use listings array if available, otherwise fall back to single listing
  const displayListings = React.useMemo(() => 
    listings.length > 0 ? listings : (listing ? [listing] : []), 
    [listings, listing]
  );
  const primaryListing = React.useMemo(() => 
    listing || (listings.length > 0 ? listings[0] : null),
    [listing, listings]
  );

  const quantities = React.useMemo(
    () => (location.state?.quantities as Record<number, number>) || {},
    [location.state]
  );

  const subtotal = React.useMemo(
    () => calculateCheckoutSubtotal(displayListings, quantities),
    [displayListings, quantities]
  );

  // Calculate delivery fee - use listing's shipping fee (no automatic calculation)

  // Track last (delivery address, seller address) so we recalc when listing gets full address after refetch
  const lastCalculatedAddressRef = useRef<string>('');
  const lastSellerAddressRef = useRef<string>('');
  const isCalculatingRef = useRef<boolean>(false);
  const [savedAddressVersion, setSavedAddressVersion] = useState(0);

  // Calculate delivery distance and availability when address or listing changes
  useEffect(() => {
    if (!primaryListing || !formData.deliveryAddress) {
      setDeliveryDistance(null);
      setDeliveryOriginUsed(null);
      setDeliveryAvailable(null);
      setCalculatingDelivery(false);
      lastCalculatedAddressRef.current = '';
      lastSellerAddressRef.current = '';
      isCalculatingRef.current = false;
      return;
    }

    const shouldCalculate = isCompleteDeliveryAddress(
      formData.addressData,
      formData.deliveryAddress
    );
    if (!shouldCalculate) {
      setDeliveryDistance(null);
      setDeliveryOriginUsed(null);
      setDeliveryAvailable(null);
      setCalculatingDelivery(false);
      lastCalculatedAddressRef.current = '';
      lastSellerAddressRef.current = '';
      isCalculatingRef.current = false;
      return;
    }

    // Resolve seller address first so we only skip when both delivery and seller are unchanged
    let sellerAddress = '';
    if (primaryListing.address && primaryListing.address.trim()) {
      sellerAddress = primaryListing.address.trim();
    } else if (primaryListing.shop_store_address && primaryListing.shop_store_address.trim()) {
      sellerAddress = primaryListing.shop_store_address.trim();
    } else if (primaryListing.city_name && primaryListing.state_abbreviation) {
      sellerAddress = `${primaryListing.city_name}, ${primaryListing.state_abbreviation}`;
    }

    // When a saved address is selected, always recalc so we never miss the initial load
    if (!showAddressForm && savedAddresses.length > 0) lastCalculatedAddressRef.current = '';
    
    const skipRecalc = lastCalculatedAddressRef.current === formData.deliveryAddress
      && lastSellerAddressRef.current === sellerAddress
      && !isCalculatingRef.current;
    if (skipRecalc) return;
    
    const calculateDelivery = async () => {
      isCalculatingRef.current = true;
      setCalculatingDelivery(true);
      try {
        // If we have seller address and it matches delivery, treat as 0 miles (skip API)
        if (sellerAddress) {
          const normalizeForCompare = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ').replace(/,\s*/g, ', ');
          const deliveryNorm = normalizeForCompare(formData.deliveryAddress);
          const sellerNorm = normalizeForCompare(sellerAddress);
          if (deliveryNorm && sellerNorm && deliveryNorm === sellerNorm) {
            setDeliveryDistance(0);
            setDeliveryAvailable(true);
            lastCalculatedAddressRef.current = formData.deliveryAddress;
            lastSellerAddressRef.current = sellerAddress;
            setCalculatingDelivery(false);
            isCalculatingRef.current = false;
            return;
          }
        }

        const deliveryForApi = (() => {
          const a = formData.addressData;
          if (a && (a.line1 || a.formatted) && a.city && a.state) {
            const parts = [(a.line1 || a.formatted || '').trim(), (a.city || '').trim(), (a.state || '').trim()].filter(Boolean);
            if ((a.postal_code || '').trim()) parts.push((a.postal_code || '').trim());
            parts.push(a.country === 'US' || !a.country ? 'USA' : (a.country || 'USA'));
            return parts.join(', ');
          }
          const raw = formData.deliveryAddress.trim().replace(/\s+/g, ' ');
          return raw ? (/\b(USA|US|United States)\b/i.test(raw) ? raw : `${raw}, USA`) : raw;
        })();

        let distance: number | null = null;
        let originUsed: string | null = null;

        // Prefer server-side calculation (no CORS, reliable geocode)
        if (deliveryForApi) {
          try {
            const feeRes = await deliveryFeeAPI.calculate(primaryListing.id, deliveryForApi, 0);
            if (feeRes?.distance != null) {
              const d = Number(feeRes.distance);
              if (Number.isFinite(d) && d >= 0) {
                distance = d;
                originUsed = feeRes?.origin_used ?? null;
              }
            }
          } catch (e) {
            if (process.env.NODE_ENV === 'development') console.warn('Delivery fee API failed, trying fallback:', e);
          }
        }

        // Fallback: client-side with seller coords + geocode proxy or Nominatim
        if (distance === null && deliveryForApi) {
          try {
            const locationRes = await deliveryFeeAPI.getListingLocation(primaryListing.id);
            const sellerLat = locationRes?.latitude != null ? Number(locationRes.latitude) : null;
            const sellerLon = locationRes?.longitude != null ? Number(locationRes.longitude) : null;
            if (Number.isFinite(sellerLat) && Number.isFinite(sellerLon)) {
              originUsed = locationRes?.origin_used ?? null;
              const geoRes = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(deliveryForApi)}&limit=1&countrycodes=us`,
                { headers: { 'User-Agent': 'LocalDash/1.0' } }
              );
              const geoData = await geoRes.json();
              if (geoData?.length > 0) {
                const dLat = parseFloat(geoData[0].lat);
                const dLon = parseFloat(geoData[0].lon);
                if (Number.isFinite(dLat) && Number.isFinite(dLon) && sellerLat != null && sellerLon != null) {
                  const d = calculateDistance(sellerLat, sellerLon, dLat, dLon);
                  distance = d < 0.1 ? 0 : Math.round(d * 100) / 100;
                }
              }
            }
          } catch (e) {
            if (process.env.NODE_ENV === 'development') console.warn('Distance calculation failed:', e);
          }
        }

        if (distance !== null) {
          setDeliveryDistance(distance);
          setDeliveryOriginUsed(originUsed);
          if (primaryListing.max_delivery_miles != null) {
            setDeliveryAvailable(distance <= primaryListing.max_delivery_miles);
          } else {
            setDeliveryAvailable(true);
          }
          lastCalculatedAddressRef.current = formData.deliveryAddress;
          lastSellerAddressRef.current = sellerAddress;
          setDeliveryCalculationFailed(false);
        } else {
          setDeliveryDistance(null);
          setDeliveryOriginUsed(null);
          setDeliveryAvailable(null);
          setDeliveryCalculationFailed(true);
          lastCalculatedAddressRef.current = '';
          lastSellerAddressRef.current = '';
        }
      } catch (err) {
        console.error('Error calculating delivery:', err);
        setDeliveryDistance(null);
        setDeliveryOriginUsed(null);
        setDeliveryAvailable(null);
        setDeliveryCalculationFailed(true);
        lastCalculatedAddressRef.current = '';
        lastSellerAddressRef.current = '';
      } finally {
        setCalculatingDelivery(false);
        isCalculatingRef.current = false;
      }
    };

    const timeoutId = setTimeout(calculateDelivery, 500);
    return () => {
      clearTimeout(timeoutId);
      isCalculatingRef.current = false;
    };
  }, [formData.deliveryAddress, formData.addressData, primaryListing, showAddressForm, savedAddresses.length, selectedAddressId, savedAddressVersion]);

  // Build full delivery instructions from address details
  const fullDeliveryInstructions = React.useMemo(() => {
    const parts: string[] = [];
    if (formData.addressType) {
      parts.push(`Address type: ${formData.addressType.charAt(0).toUpperCase() + formData.addressType.slice(1)}`);
    }
    if (formData.aptSuite) parts.push(`Apt/Suite: ${formData.aptSuite}`);
    if (formData.entryCode) parts.push(`Entry code: ${formData.entryCode}`);
    if (formData.buildingName) parts.push(`Building: ${formData.buildingName}`);
    if (formData.gateCode) parts.push(`Gate code: ${formData.gateCode}`);
    if (formData.businessName) parts.push(`Business: ${formData.businessName}`);
    if (formData.hotelName) parts.push(`Hotel: ${formData.hotelName}`);
    if (formData.roomSuite) parts.push(`Room: ${formData.roomSuite}`);
    const leaveLabels: Record<string, string> = {
      apartment_door: 'Leave at apartment door',
      lobby: 'Leave at lobby',
      office_suite: 'Leave at office suite/floor',
      room_door: 'Leave at room door',
    };
    if (formData.leaveLocation && leaveLabels[formData.leaveLocation]) {
      parts.push(leaveLabels[formData.leaveLocation]);
    }
    if (formData.deliveryInstructions?.trim()) parts.push(formData.deliveryInstructions.trim());
    return parts.join('. ');
  }, [formData.addressType, formData.aptSuite, formData.entryCode, formData.buildingName, formData.gateCode, formData.businessName, formData.hotelName, formData.roomSuite, formData.leaveLocation, formData.deliveryInstructions]);

  // Handle address change from address form
  const handleAddressChange = React.useCallback((addressData: { line1: string; city: string; state: string; postal_code: string; country: string; formatted: string }, phone: string, isComplete: boolean) => {
    const formattedAddress = addressData.formatted || '';
    
    setFormData(prev => ({
      ...prev,
      deliveryAddress: formattedAddress || prev.deliveryAddress,
      phoneNumber: phone || prev.phoneNumber,
      addressData: addressData
    }));
    
    setAddressComplete(isComplete);
  }, []);


  const zipCode = React.useMemo(
    () => extractPostalCodeFromAddress(formData.addressData, formData.deliveryAddress),
    [formData.addressData, formData.deliveryAddress]
  );

  const deliveryFee = React.useMemo(() => {
    if (displayListings.length === 0) return 2.99;
    return calculateDeliveryFeeIos(displayListings[0], deliveryDistance);
  }, [displayListings, deliveryDistance]);

  const taxState = React.useMemo(
    () =>
      resolveTaxStateIos(
        formData.addressData?.state || '',
        resolvedStateFromZip,
        zipCode
      ),
    [formData.addressData?.state, resolvedStateFromZip, zipCode]
  );

  const salesTax = React.useMemo(
    () =>
      calculateSalesTaxAmount(
        subtotal,
        deliveryFee,
        taxState,
        !!formData.deliveryAddress?.trim()
      ),
    [subtotal, deliveryFee, taxState, formData.deliveryAddress]
  );

  const total = React.useMemo(
    () => calculateCheckoutTotal(subtotal, deliveryFee, salesTax),
    [subtotal, deliveryFee, salesTax]
  );

  const cardPaymentAmount = total;

  // iOS: loadStateFromZip when stateField empty and zip is 5 digits
  useEffect(() => {
    if ((formData.addressData?.state || '').trim()) {
      return;
    }
    if (!zipCode || zipCode.length !== 5) {
      setResolvedStateFromZip(null);
      return;
    }
    let cancelled = false;
    locationsAPI.lookupZipCode(zipCode).then((data: any) => {
      if (!cancelled && data?.state_abbreviation) {
        setResolvedStateFromZip(data.state_abbreviation);
      }
    }).catch(() => {
      if (!cancelled) setResolvedStateFromZip(null);
    });
    return () => { cancelled = true; };
  }, [formData.addressData?.state, zipCode]);

  const hasCompleteDeliveryAddress = React.useMemo(
    () => isCompleteDeliveryAddress(formData.addressData, formData.deliveryAddress),
    [formData.addressData, formData.deliveryAddress]
  );

  // Validate delivery form — full street address required (not city/area only)
  const isDeliveryValid = React.useMemo(() => {
    return !!(formData.fullName.trim() && hasCompleteDeliveryAddress);
  }, [formData.fullName, hasCompleteDeliveryAddress]);

  const handlePaymentSuccess = React.useCallback(async () => {
    if (displayListings.length === 0) return;
    
    // Check if delivery is available before completing payment
    if (deliveryAvailable === false) {
      setError('Cannot complete payment: Delivery is not available to this address. Please choose a different address within the delivery zone.');
      toast.error('Delivery is not available to this address');
      return;
    }
    // Require distance to be calculated when delivery address is present
    if (formData.deliveryAddress?.trim() && (deliveryDistance == null || deliveryCalculationFailed)) {
      setError('Cannot place order: Delivery distance could not be calculated. Please check your address or try again.');
      toast.error('Delivery distance must be calculated before placing order');
      return;
    }
    
    // Save shipping address if requested (to user profile - Settings > Shipping Addresses)
    if (saveAddress && hasCompleteDeliveryAddress && isAuthenticated) {
      try {
        const addr = formData.addressData;
        const payload = {
          line1: addr?.line1 ?? formData.deliveryAddress,
          city: addr?.city ?? '',
          state: addr?.state ?? '',
          postal_code: addr?.postal_code ?? '',
          country: addr?.country ?? 'US',
          address: addr?.formatted ?? formData.deliveryAddress,
          instructions: fullDeliveryInstructions || formData.deliveryInstructions,
          phone_number: formData.phoneNumber,
          address_type: saveAddress ? 'other' : (formData.addressType || 'other'),
          apt_suite: formData.aptSuite || undefined,
          entry_code: formData.entryCode || undefined,
          building_name: formData.buildingName || undefined,
        };
        const isDefault = saveAsDefault || savedAddresses.length === 0;
        const addressIdToUpdate = editingAddressId ?? selectedAddressId;
        if (addressIdToUpdate) {
          await shippingAddressesAPI.update(addressIdToUpdate, { ...payload, is_default: isDefault });
          toast.success('Address updated in your profile');
          await loadSavedAddresses();
        } else if (!isDuplicateAddress(savedAddresses, payload.line1, payload.city, payload.state, payload.postal_code, null)) {
          await shippingAddressesAPI.create({
            ...payload,
            is_default: isDefault,
          });
          toast.success('Address saved to your profile for easy checkout next time');
          await loadSavedAddresses();
        } else {
          toast.error('This address is already saved');
        }
      } catch (err: any) {
        console.error('Error saving shipping address:', err);
        const msg = err.response?.data?.error || err.response?.data?.details || err.message || 'Could not save address';
        toast.error(msg);
      }
    }
    
    // Purchase is now created in the payment confirmation
    // Navigate to my-orders page where user can track their order
    navigate('/my-orders');
  }, [displayListings, primaryListing, formData, fullDeliveryInstructions, navigate, saveAddress, saveAsDefault, isAuthenticated, savedAddresses, deliveryAvailable, deliveryDistance, deliveryCalculationFailed, editingAddressId, selectedAddressId, loadSavedAddresses, hasCompleteDeliveryAddress]);

  const CheckoutContent = React.useMemo(() => {
    const quantities = (location.state?.quantities as Record<number, number>) || {};
    const itemCount = displayListings.reduce((sum, item) => sum + (quantities[item.id] || 1), 0);
    return (
      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <h1 className="text-2xl font-bold mb-6">Checkout</h1>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column: form */}
            <div className="lg:col-span-2 space-y-6">
            {/* === Delivery Address - always on page === */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <i className="ri-map-pin-line text-green-600"></i>
                Delivery Address
              </h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    id="fullName"
                    autoComplete="name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                    required
                  />
                </div>

                {/* While saved addresses load, show spinner — not the empty address form */}
                {isAuthenticated && loadingAddresses ? (
                  <div className="flex items-center gap-3 py-4 text-gray-600">
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-green-600" />
                    <span className="text-sm">Loading address…</span>
                  </div>
                ) : (
                  <>
                {/* Saved address + Change → opens dialog with radio list (mobile-style) */}
                {!showAddressForm && savedAddresses.length > 0 && (
                  <div className="space-y-3">
                    {formData.deliveryAddress ? (
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 relative">
                        <div className="pr-20">
                          <p className="font-medium text-gray-900">
                            {addressStreetLineOnly(formData.addressData, formData.deliveryAddress)}
                          </p>
                          {formData.deliveryInstructions && (
                            <p className="text-sm text-gray-600 mt-1">Instructions: {formData.deliveryInstructions}</p>
                          )}
                          {formData.phoneNumber && (
                            <p className="text-sm text-gray-600">Phone: {formData.phoneNumber}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowAddressModal(true)}
                          className="absolute top-3 right-3 text-green-600 hover:text-green-700 text-sm font-medium"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowAddressModal(true)}
                        className="flex items-center gap-2 text-green-600 hover:text-green-700 font-medium text-sm"
                      >
                        <i className="ri-map-pin-line"></i>
                        Select or add delivery address
                      </button>
                    )}
                    <HomeLocationModal
                      isOpen={showAddressModal}
                      onClose={() => setShowAddressModal(false)}
                      checkoutMode
                      checkoutSelectedAddressId={selectedAddressId}
                      onCheckoutAddressSelect={(data) => {
                        setFormData(prev => ({
                          ...prev,
                          deliveryAddress: data.deliveryAddress,
                          deliveryInstructions: data.deliveryInstructions,
                          phoneNumber: data.phoneNumber,
                          addressType: (data.addressType as 'house' | 'apartment' | 'hotel' | 'office' | 'other') ?? null,
                          aptSuite: data.aptSuite,
                          entryCode: data.entryCode,
                          buildingName: data.buildingName,
                          addressData: data.addressData,
                        }));
                        setSelectedAddressId(data.addressId ?? null);
                        setEditingAddressId(data.addressId ?? null);
                        setShowAddressModal(false);
                        setSavedAddressVersion(v => v + 1);
                      }}
                    />
                  </div>
                )}

                {/* Address form with Google autocomplete */}
                {(showAddressForm || savedAddresses.length === 0) && (
                  <AddressFormWithAutocomplete
                    onAddressChange={handleAddressChange}
                    initialAddress={formData.addressData || undefined}
                    initialPhone={formData.phoneNumber}
                  />
                )}
                  </>
                )}
                {formData.deliveryAddress.trim() && !hasCompleteDeliveryAddress && !loadingAddresses && (
                  <p className="text-sm text-amber-800 mt-2">
                    Enter a complete street address with city, state, and ZIP. City or area only cannot be used for delivery.
                  </p>
                )}
              </div>
            </div>

            {/* === Delivery Preferences - always on page === */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <i className="ri-truck-line text-green-600"></i>
                Delivery Preferences
              </h2>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, deliveryPreference: 'leave_at_door' }))}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all font-medium text-sm ${
                      formData.deliveryPreference === 'leave_at_door'
                        ? 'border-green-600 bg-green-50 text-green-800'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i className="ri-door-open-line text-xl"></i>
                    Leave at door
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, deliveryPreference: 'meet_at_door' }))}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all font-medium text-sm ${
                      formData.deliveryPreference === 'meet_at_door'
                        ? 'border-green-600 bg-green-50 text-green-800'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i className="ri-hand-heart-line text-xl"></i>
                    Meet at door
                  </button>
                </div>
                <textarea
                  name="deliveryInstructions"
                  value={formData.deliveryInstructions}
                  onChange={handleInputChange}
                  placeholder="Delivery instructions (apt #, gate code, leave at door, etc.)"
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                />
                {isAuthenticated && !savedAddresses.some((a: any) => a.is_default) && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveAddress}
                      onChange={(e) => setSaveAddress(e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    Save address for next time
                  </label>
                )}
              </div>
            </div>

            {/* === Payment - always on page === */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <i className="ri-bank-card-line text-green-600"></i>
                Payment
              </h2>
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <i className="ri-shield-check-line text-xl text-green-600"></i>
                  <div>
                    <p className="text-sm font-medium text-green-900">Secure Checkout</p>
                    <p className="text-xs text-green-700">Processed securely by Stripe &middot; SSL encrypted</p>
                  </div>
                </div>
              </div>

              {deliveryAvailable === false ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">
                    Cannot proceed: delivery is not available to this address.
                  </p>
                </div>
              ) : !isDeliveryValid ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium">
                    Enter a complete delivery address (street, city, state, and ZIP) to continue to payment.
                  </p>
                </div>
              ) : formData.deliveryAddress?.trim() && (deliveryDistance == null || deliveryCalculationFailed) ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium">
                    Cannot place order until delivery distance is calculated. Please check your address or try again.
                  </p>
                </div>
              ) : primaryListing ? (
                stripePromise ? (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-3">Enter your card details:</p>
                    <CardPaymentForm
                      amount={cardPaymentAmount}
                      listingId={primaryListing.id}
                      deliveryAddress={formData.deliveryAddress}
                      addressData={formData.addressData}
                      deliveryInstructions={fullDeliveryInstructions || formData.deliveryInstructions}
                      deliveryPreference={formData.deliveryPreference}
                      priceBreakdown={{
                        item_price: subtotal,
                        shipping_fee: deliveryFee,
                        sales_tax: salesTax,
                        total_amount: total,
                        quantity: (() => {
                          const q = location.state?.quantities as Record<number, number> | undefined;
                          return q ? Object.values(q).reduce((a: number, b: number) => a + b, 0) : displayListings.length;
                        })(),
                        listings: displayListings.length > 1 ? displayListings.map((l) => ({
                          listing_id: l.id,
                          quantity: (location.state?.quantities as Record<number, number>)?.[l.id] ?? 1,
                        })) : undefined,
                      }}
                      onSuccess={handlePaymentSuccess}
                      onError={(err) => setError(err)}
                    />
                  </div>
                ) : !stripeConfigReady ? (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">Loading payment form…</p>
                  </div>
                ) : (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium text-amber-900">Card payments could not load</p>
                    <p className="text-sm text-amber-800 mt-1">
                      Make sure the API is running (<code className="text-xs">server/.env</code> has{' '}
                      <code className="text-xs">STRIPE_SECRET_KEY</code>) and restart{' '}
                      <code className="text-xs">npm start</code> in <code className="text-xs">client/</code>.
                    </p>
                  </div>
                )
              ) : null}

              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>

            </div>
            {/* Right column: order summary + delivery */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-4 space-y-4">
                <div className="bg-white rounded-xl shadow-sm p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-shopping-bag-line text-green-600"></i>
                    Order Summary
                  </h2>
                  {primaryListing && (
                    <div className="mb-4 pb-4 border-b border-gray-200">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Your order from</p>
                      <p className="text-base font-semibold text-gray-900 mt-1">{displayListings.length > 0 && displayListings.every(l => l.store_id && l.store_id === displayListings[0].store_id) ? (primaryListing.store_name || primaryListing.shop_store_name || 'Store') : (primaryListing.seller_name || 'Seller')}</p>
                      {(primaryListing.city_name || primaryListing.state_abbreviation) && (
                        <p className="text-sm text-gray-500 mt-0.5">
                          {[primaryListing.city_name, primaryListing.state_abbreviation].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                  {/* Delivery status */}
                  {formData.deliveryAddress ? (
                    <div className="mb-4">
                      {calculatingDelivery ? (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                          <p className="text-sm text-gray-600">Calculating delivery...</p>
                        </div>
                      ) : deliveryAvailable === false ? (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
                          <i className="ri-close-circle-line text-red-600 text-xl mr-2"></i>
                          <div>
                            <p className="text-sm font-semibold text-red-800">Delivery Not Available</p>
                            <p className="text-xs text-red-700">
                              {deliveryDistance != null ? (deliveryDistance === 0 ? '0' : deliveryDistance.toFixed(2)) : '—'} mi away (max {primaryListing?.max_delivery_miles} mi)
                            </p>
                          </div>
                        </div>
                      ) : deliveryAvailable === true && deliveryDistance !== null ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
                          <i className="ri-checkbox-circle-line text-green-600 text-xl mr-2"></i>
                          <div>
                            <p className="text-sm font-semibold text-green-800">Delivery Available</p>
                            <p className="text-xs text-green-700">
                              {deliveryDistance === 0 ? '0' : deliveryDistance.toFixed(2)} mi away &middot; Fee: ${deliveryFee.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ) : deliveryCalculationFailed ? (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-800">Could not calculate distance. You cannot place the order until the address is valid and distance is calculated.</p>
                        </div>
                      ) : (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm text-gray-600">Checking availability...</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-4">Add delivery address to see availability</p>
                  )}
                  {/* Items */}
                  <div className="space-y-3 mb-4">
                    {displayListings.map((item) => {
                      const quantities = location.state?.quantities || {};
                      const quantity = quantities[item.id] || 1;
                      return (
                        <div key={item.id} className="flex items-center gap-3">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.title} className="w-14 h-14 object-cover rounded-lg" />
                          ) : (
                            <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center text-xl">📦</div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{item.title}</p>
                            {item.category && <p className="text-xs text-gray-500 capitalize">{item.category}</p>}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold">
                              {formatPrice(checkoutLineTotal(item, quantity))}
                            </p>
                            {quantity > 1 && (
                              <p className="text-xs text-gray-500">
                                {formatPrice(item.price)} × {quantity}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Price breakdown */}
                  <div className="border-t pt-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal ({itemCount} item{itemCount === 1 ? '' : 's'})</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    {formData.deliveryAddress?.trim() ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Delivery
                            {calculatingDelivery ? ' (calculating…)' : ''}
                          </span>
                          <span>${deliveryFee.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tax</span>
                          <span>${salesTax.toFixed(2)}</span>
                        </div>
                        {deliveryCalculationFailed && deliveryDistance === null && (
                          <p className="text-xs text-amber-700">
                            Could not calculate delivery distance for this address
                          </p>
                        )}
                        <div className="border-t pt-2 mt-2 flex justify-between font-bold text-base">
                          <span>Total</span>
                          <span>${total.toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-500 text-xs pt-1">
                        Add delivery address to see fee and total
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [displayListings, formData, error, handleInputChange, handlePaymentSuccess, primaryListing, subtotal, deliveryFee, total, cardPaymentAmount, isAuthenticated, navigate, formatPrice, handleAddressChange, addressComplete, addressValue, savedAddresses, saveAddress, loadingAddresses, deliveryAvailable, deliveryDistance, deliveryCalculationFailed, deliveryOriginUsed, calculatingDelivery, showAddressForm, showAddressModal, selectedAddressId, editingAddressId, addressStep, fullDeliveryInstructions, setAddressStep, salesTax, location.state, stripePromise, stripeConfigReady, stripeMode, isDeliveryValid, hasCompleteDeliveryAddress]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!primaryListing && listings.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600">No items found</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Wrap with Elements only if Stripe is available
  if (stripePromise) {
    return (
      <Elements stripe={stripePromise} options={{ locale: 'en' }}>
        {CheckoutContent}
      </Elements>
    );
  }

  // Fallback if Stripe is not available - only show cash payment
  return <>{CheckoutContent}</>;
};

export default Checkout;


